import React, { useCallback, useState, Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { Box, Divider, CircularProgress, Drawer, Typography } from '@material-ui/core'
import mammoth from 'mammoth'
import i18n from 'common/util/internationalization'

import { useStyles, TEMPLATE_HEADER_LINK, DeleteButton } from './templateDrawerStyles'
import { getTemplateCustomizationUrl } from 'common/util/urlMap'
import { getLocalStorageItem } from 'businesslayer/minutesLocalStore'
import { getLocalizedLanguage } from 'common/util/langMap'
import { UploadStatus } from './UploadStatus'
import { addKeyPressHandler } from 'common/util/functions'
import containsHTML from 'components/minutetaker/components/Utils/Utils'
import {
    MAX_FILE_SIZE,
    validTypes,
    MAX_FILENAME_LENGTH,
    MAX_TEMPLATE_TITLE_LENGTH,
    MAX_TEMPLATE_DESCRIPTION_LENGTH,
    MAX_TEMPLATE_TITLE_LENGTH_IN_SIDEBAR
} from 'common/constants/constants'
import {
    saveTemplateToServer,
    updateTemplateMetadata,
    deleteTemplate
} from 'businesslayer/networkrequests/templateService'

// Constants

import { TEMPLATE_UPLOAD_STATUS } from '../../../src/components/minutetaker/components/InvitationView/types'

// Icons
import { AtlasIcon } from '@diligentcorp/atlas-react'
import { IconButton } from 'components/shared/StyledComponents'

//Components
import { AtlasButton } from 'components/atlas-material/button'
import { AddActionEditActionButton } from 'components/shared/StyledComponents'

//Styles
import { ClassNames } from '@emotion/react'
import ConfirmationDialog from 'components/shared/ConfirmationDialog'
import { translate } from 'components/shared/internationalization'

interface FileItem {
    name: string
    size: number
    file: File | null
}

interface TemplateDrawerProps {
    setIsOpen: Dispatch<SetStateAction<boolean>>
    isOpen: boolean
    items?: FileItem | null
    setItems?: Dispatch<SetStateAction<FileItem | null>>
    uploadStatus?: TEMPLATE_UPLOAD_STATUS | null
    setUploadStatus?: Dispatch<SetStateAction<TEMPLATE_UPLOAD_STATUS | null>>
    reloadTemplateList?: (string) => void
    downloadTemplateFile?: (id: string, templateTitle: string) => void
    resetPaginationAndReload?: () => void
    fileUploadError?: string | null
    templateList?: any
    systemTemplate?: any
    isUpdateTemplateClicked?: boolean
    setShowToast?: Dispatch<SetStateAction<boolean>>
    setToastType?: Dispatch<SetStateAction<string>>
    setToastMessage?: Dispatch<SetStateAction<string>>
    setTemplateAdded?: Dispatch<SetStateAction<boolean>>
    onTemplateAdded?: () => void
}

const TemplateDrawer: React.FC<TemplateDrawerProps> = ({
    setIsOpen,
    isOpen,
    items,
    setItems,
    uploadStatus,
    setUploadStatus,
    fileUploadError,
    templateList,
    reloadTemplateList,
    downloadTemplateFile,
    resetPaginationAndReload,
    isUpdateTemplateClicked,
    systemTemplate,
    setShowToast,
    setToastType,
    setToastMessage,
    setTemplateAdded,
    onTemplateAdded
}) => {
    const classes = useStyles()

    // Get user language for help URL
    const userLang = getLocalStorageItem('userLanguage') || 'en'
    const localizedLang = getLocalizedLanguage(userLang)

    const toggleDrawer = useCallback(() => {
        setIsOpen(!isOpen)
        resetUpload()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setIsOpen, isOpen])

    const [title, setTitle] = useState('')
    const [headerTitle, setHeaderTitle] = useState('')
    const [description, setDescription] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [templateDescription, setTemplateDescription] = useState('')
    const [serverValidationErrors, setServerValidationErrors] = useState<string[]>([])

    useEffect(() => {
        setTitle(templateList?.title)
        setHeaderTitle(templateList?.title)
        //everytime when the isOpen changes, we set the description
        setDescription(templateList?.description !== undefined ? templateList?.description : '')
        setTemplateDescription(
            templateList?.description !== undefined ? templateList?.description : ''
        )
    }, [templateList, isOpen])

    const [showConfirmation, setShowConfirmation] = useState(false)

    const handleTitleChange = (event) => {
        setTitle(event.target.value)
    }
    const handleDescriptionChange = (event) => {
        setDescription(event.target.value)
    }

    const resetUpload = () => {
        if (setItems) setItems({ name: '', size: 0, file: null })
        if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.DEFAULT)
        // Clear description when file is deleted
        setDescription('')
        setServerValidationErrors([])
    }

    const handleDrop = (e) => {
        e.preventDefault()
        const file = e.dataTransfer?.files?.[0]
        if (!file) return

        const validTypes = ['.docx']
        const ext = `.${file.name.split('.').pop()}`

        if (validTypes.includes(ext)) {
            handleSelectTemplateFile({ target: { files: [file] } })
        } else {
            if (setItems) setItems({ name: file.name, size: file.size, file: file })
            if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.UNSUPPORTED_FORMAT)
        }
    }

    const handleSaveTemplate = async () => {
        setIsSubmitting(true)
        setSubmitError(null)

        try {
            if (!items?.file) {
                const errorMsg = 'No file selected'
                if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.SAVE_ERROR)
                return { success: false, error: errorMsg }
            }

            const formData = new FormData()
            formData.append('title', title)
            formData.append('description', description)
            formData.append('file', items?.file)
            const result = await saveTemplateToServer(formData)

            if (result.success) {
                // Handle success
                if (setShowToast) setShowToast(true)
                if (setToastType) setToastType('success')
                if (setToastMessage) setToastMessage(i18n.t('TEMPLATE_ADD_SUCCESS'))
                if (setTemplateAdded) setTemplateAdded(true)
                if (onTemplateAdded) {
                    onTemplateAdded()
                }
                toggleDrawer()
            } else if (
                // Validation errors coming from template service (normalized fields or raw errorData)
                [
                    'FOUND_INCORRECT_BLOCK_TAG_MATCHING',
                    'FOUND_INVALID_TAGS_IN_TEMPLATE',
                    'AT_LEAST_ONE_SUPPORTED_TAG_REQUIRED'
                ].includes(result?.errorCode as any) ||
                Array.isArray(result?.validationErrors) ||
                [
                    'FOUND_INCORRECT_BLOCK_TAG_MATCHING',
                    'FOUND_INVALID_TAGS_IN_TEMPLATE',
                    'AT_LEAST_ONE_SUPPORTED_TAG_REQUIRED'
                ].includes((result?.errorData as any)?.message) ||
                Array.isArray((result?.errorData as any)?.errors)
            ) {
                const errorsArray: string[] = Array.isArray(result?.validationErrors)
                    ? (result?.validationErrors as string[])
                    : Array.isArray((result?.errorData as any)?.errors)
                    ? ((result?.errorData as any)?.errors as string[])
                    : []
                const isTagMismatch = [
                    'FOUND_INCORRECT_BLOCK_TAG_MATCHING',
                    'FOUND_INVALID_TAGS_IN_TEMPLATE',
                    'AT_LEAST_ONE_SUPPORTED_TAG_REQUIRED'
                ].includes((result?.errorCode as any) || (result?.errorData as any)?.message)
                if (isTagMismatch || errorsArray.length) {
                    setServerValidationErrors(errorsArray)
                }
                if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.SAVE_ERROR)
                if (setShowToast) setShowToast(true)
                if (setToastType) setToastType('error')
                if (setToastMessage) setToastMessage(i18n.t('TEMPLATE_ADD_FAIL'))
            } else {
                // Handle other errors - set generic failed status
                if (setUploadStatus)
                    setUploadStatus(
                        result.error === 'AT_LEAST_ONE_SUPPORTED_TAG_REQUIRED'
                            ? TEMPLATE_UPLOAD_STATUS.SAVE_ERROR
                            : TEMPLATE_UPLOAD_STATUS.FAILED
                    )
                if (setShowToast) setShowToast(true)
                if (setToastType) setToastType('error')
                if (setToastMessage) setToastMessage(i18n.t('TEMPLATE_ADD_FAIL'))
            }

            return result
        } catch (error) {
            // Handle error - set upload status to show error below file
            const errorMsg = error.message || 'Failed to save template'
            if (setUploadStatus)
                setUploadStatus(
                    errorMsg === 'AT_LEAST_ONE_SUPPORTED_TAG_REQUIRED'
                        ? TEMPLATE_UPLOAD_STATUS.SAVE_ERROR
                        : TEMPLATE_UPLOAD_STATUS.FAILED
                )
            if (setShowToast) setShowToast(true)
            if (setToastType) setToastType('error')
            if (setToastMessage) setToastMessage(i18n.t('TEMPLATE_ADD_FAIL'))
            return { success: false, error: errorMsg }
        } finally {
            // Always reset submitting state
            setIsSubmitting(false)
        }
    }

    const handleSelectTemplateFile = async (event) => {
        const file = event.target.files?.[0]
        if (!file) return

        const { name, size } = file
        const ext = `.${name.split('.').pop()}`

        if (setItems) setItems({ name, size, file })

        if (!validTypes.includes(ext)) {
            if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.UNSUPPORTED_FORMAT)
            return
        }

        if (size > MAX_FILE_SIZE) {
            if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.FILE_SIZE_LIMIT)
            return
        }

        if (name?.length > MAX_FILENAME_LENGTH) {
            if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.FILE_NAME_LENGTH)
            return
        }

        // Show progress spinner
        if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.PROGRESS)

        const reader = new FileReader()
        reader.onload = async (e) => {
            try {
                let text = ''

                if (file.name.endsWith('.docx')) {
                    // Use mammoth to extract text from DOCX
                    const arrayBuffer = e.target?.result as ArrayBuffer
                    const result = await mammoth.extractRawText({ arrayBuffer })
                    text = result.value || ''
                } else {
                    // For other file types, use the text content directly
                    text = (e.target?.result as string) || ''
                }

                // Check if file is empty
                if (!text.trim()) {
                    if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.EMPTY_FILE)
                    return
                }

                // Check for HTML content
                if (containsHTML(text)) {
                    if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.HTML_CONTENT)
                    return
                }

                // File is valid, proceed with upload
                if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.SUCCESS)
                // Remove file extension from title
                const fileNameWithoutExtension = name.replace(/\.[^/.]+$/, '')
                setTitle(fileNameWithoutExtension)
            } catch (error) {
                console.error('Error processing file:', error)
                if (setUploadStatus) setUploadStatus(TEMPLATE_UPLOAD_STATUS.FAILED)
            }
        }

        file.name.endsWith('.docx') ? reader.readAsArrayBuffer(file) : reader.readAsText(file)
    }

    const handleUpdateTemplate = async (
        templateId: string,
        templateList: { title: string; description: string }
    ) => {
        setIsSubmitting(true)
        setSubmitError(null)

        try {
            // Create object for metadata update
            const templateData = {
                title: templateList.title,
                description: templateList.description
            }

            // Update metadata using object (not FormData)
            const result = await updateTemplateMetadata(templateId, templateData)

            if (result.success) {
                // Handle success
                if (setTemplateAdded) setTemplateAdded(true)
                toggleDrawer()

                if (resetPaginationAndReload) resetPaginationAndReload()
            } else {
                // Handle error
                setSubmitError(result.error || 'Failed to update template')
            }
        } catch (error) {
            setSubmitError(error.message || 'Failed to update template')
        } finally {
            // Always reset submitting state
            setIsSubmitting(false)
        }
    }

    //delete template from drawer
    const deleteTemplateFromDrawer = useCallback(async () => {
        if (templateList?.id) {
            try {
                const res = await deleteTemplate(templateList?.id)
                if (res.message === 'DELETION_SUCCESS') {
                    if (setShowToast) setShowToast(true)
                    if (setToastType) setToastType('success')
                    if (setToastMessage) setToastMessage(i18n.t('TEMPLATE_DELETE_SUCCESS'))
                } else {
                    if (setShowToast) setShowToast(true)
                    if (setToastType) setToastType('error')
                    if (setToastMessage) setToastMessage(i18n.t('TEMPLATE_DELETE_FAIL'))
                }
                if (reloadTemplateList) reloadTemplateList(templateList?.id)
            } catch (error) {
                if (setShowToast) setShowToast(true)
                if (setToastType) setToastType('error')
                if (setToastMessage) setToastMessage(i18n.t('TEMPLATE_DELETE_FAIL'))
                console.error('Error deleting template:', error)
            }
        }

        // Close drawer after setting toast state
        toggleDrawer()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadTemplateList, templateList?.id])

    const renderConfirmedAction = useMemo(
        () => (
            <ConfirmationDialog
                message={
                    templateList?.isDefault
                        ? i18n.t('MINUTES_TEMPLATES_DELETE_DEFAULT_TEMPLATE_MESSAGE')
                        : i18n.t('MINUTES_TEMPLATES_DELETE_TEMPLATE_MESSAGE')
                }
                title={
                    templateList?.isDefault
                        ? i18n.t('MINUTES_TEMPLATES_DELETE_PRFERED_TEMPLATE_TITLE')
                        : i18n.t('MINUTES_TEMPLATES_DELETE_TEMPLATE_TITLE')
                }
                onConfirm={deleteTemplateFromDrawer}
                onCancel={() => setShowConfirmation(false)}
                confirmLabel={translate('DELETE') as unknown as string}
            />
        ),
        [deleteTemplateFromDrawer, setShowConfirmation, templateList?.isDefault] // eslint-disable-line react-hooks/exhaustive-deps
    )

    const confirmationDialog = showConfirmation ? renderConfirmedAction : null

    const returnTitleErrorMessage = useMemo(() => {
        if (!title || title.trim() === '') {
            return i18n.t('INVALID_MINUTES_TITLE.required')
        } else if (title?.length > MAX_TEMPLATE_TITLE_LENGTH) {
            return i18n.t('MINUTES_TEMPLATES_TITLE_MAX_LENGTH')
        } else if (containsHTML(title || '')) {
            return i18n.t('NO_HTML_MESSAGE')
        } else if (
            /^[\d\s\W]*(legacy\s+diligent\s+template|legacy\s+diligent+template|legacy+diligent\s+template|legacydiligenttemplate|board\s+meeting\s+minutes\s+template|board\s+meeting\s+minutes+template|board+meeting\s+minutes\s+template|boardmeetingminutestemplate)[\d\s\W]*$/i.test(
                title || ''
            )
        ) {
            return i18n.t('MINUTES_TEMPLATES_TITLE_RESERVED_ERROR')
        } else if (submitError?.length !== 0) {
            if (submitError === 'AT_LEAST_ONE_SUPPORTED_TAG_REQUIRED') {
                return ''
            } else {
                return submitError
            }
        }
        return ''
    }, [title, submitError])

    const returnDescriptionErrorMessage = useMemo(() => {
        if (description?.length > MAX_TEMPLATE_DESCRIPTION_LENGTH) {
            return i18n.t('MINUTES_TEMPLATES_DESCRIPTION_MAX_LENGTH')
        } else if (containsHTML(description || '')) {
            return i18n.t('NO_HTML_MESSAGE')
        } else if (submitError?.length !== 0) {
            if (submitError === 'AT_LEAST_ONE_SUPPORTED_TAG_REQUIRED') {
                return ''
            } else {
                return submitError
            }
        }
        return ''
    }, [description, submitError])

    const renderSystemTemplateText = (showTitle) => {
        return (
            <>
                {showTitle
                    ? headerTitle && headerTitle.length > MAX_TEMPLATE_TITLE_LENGTH_IN_SIDEBAR
                        ? `${headerTitle.slice(0, MAX_TEMPLATE_TITLE_LENGTH_IN_SIDEBAR)}...`
                        : headerTitle
                    : templateDescription &&
                      templateDescription.length > MAX_TEMPLATE_TITLE_LENGTH_IN_SIDEBAR
                    ? `${templateDescription.slice(0, MAX_TEMPLATE_TITLE_LENGTH_IN_SIDEBAR)}...`
                    : templateDescription}
            </>
        )
    }

    return (
        <>
            <Drawer
                className={classes.drawer}
                classes={{ paper: classes.drawerPaper }}
                anchor="right"
                open={isOpen}
                onClose={toggleDrawer}
            >
                <Box className={classes.headerBox}>
                    {isUpdateTemplateClicked ? (
                        <Typography className={classes.headerTitle}>
                            {i18n.t('MINUTES_TEMPLATES_EDIT_TEMPLATE')}
                        </Typography>
                    ) : (
                        <Typography className={classes.headerTitle}>
                            {i18n.t('MINUTES_TEMPLATES_ADD_TEMPLATES')}
                        </Typography>
                    )}
                    <Box className={classes.btnContainer}>
                        <IconButton
                            disableFocusRipple
                            disableRipple
                            onClick={() => toggleDrawer()}
                            data-analytics="MM-Template-Close"
                        >
                            <div
                                className={classes.closeButton}
                                role="button"
                                aria-label="Close"
                                tabIndex={0}
                                onClick={() => toggleDrawer()}
                                onKeyUp={(e) => {
                                    if (e.key === ' ' || e.key === 'Enter') {
                                        e.preventDefault()
                                        toggleDrawer()
                                    }
                                }}
                            >
                                <AtlasIcon data-testid="AtlasModal_CloseIcon" name="close" />
                            </div>
                        </IconButton>
                    </Box>
                </Box>
                <Box className={classes.body}>
                    <Box className={classes.contentBox}>
                        <Box className={classes.defaultTemplateSection}>
                            {!isUpdateTemplateClicked && (
                                <Typography className={classes.templateTitle}>
                                    {i18n.t('MINUTES_TEMPLATES_DRAWER_INTRO')}
                                    <TEMPLATE_HEADER_LINK
                                        href={getTemplateCustomizationUrl(localizedLang)}
                                        target="blank"
                                    >
                                        {i18n.t('MINUTES_TEMPLATES_LEARN_MORE')}
                                    </TEMPLATE_HEADER_LINK>
                                    <br />
                                    <span
                                        onClick={(e) => {
                                            const target = e.target as HTMLElement
                                            if (target.tagName === 'A') {
                                                e.preventDefault()
                                                downloadTemplateFile &&
                                                    (isUpdateTemplateClicked
                                                        ? downloadTemplateFile(
                                                              templateList?.id,
                                                              templateList?.title
                                                          )
                                                        : downloadTemplateFile(
                                                              systemTemplate[0].id,
                                                              systemTemplate[0].title
                                                          ))
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            const target = e.target as HTMLElement
                                            if (
                                                (e.key === 'Enter' || e.key === ' ') &&
                                                target.tagName === 'A'
                                            ) {
                                                e.preventDefault()
                                                downloadTemplateFile &&
                                                    (isUpdateTemplateClicked
                                                        ? downloadTemplateFile(
                                                              templateList?.id,
                                                              templateList?.title
                                                          )
                                                        : downloadTemplateFile(
                                                              systemTemplate[0].id,
                                                              systemTemplate[0].title
                                                          ))
                                            }
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: i18n.t('MINUTES_TEMPLATES_DRAWER_GUIDE_TEXT', {
                                                diligentTemplateLink: '#',
                                                diligentTemplateClass: '',
                                                templateDownloadLink: `${classes.templateDownloadLink}`
                                            })
                                        }}
                                    ></span>
                                </Typography>
                            )}
                            <Box className={classes.uploadSection}>
                                <UploadStatus
                                    uploadStatus={uploadStatus}
                                    items={items}
                                    fileUploadError={fileUploadError}
                                    validationErrors={serverValidationErrors}
                                    isUpdateTemplateClicked={isUpdateTemplateClicked}
                                    title={title}
                                    description={description}
                                    templateList={templateList}
                                    returnTitleErrorMessage={returnTitleErrorMessage}
                                    returnDescriptionErrorMessage={returnDescriptionErrorMessage}
                                    onTitleChange={handleTitleChange}
                                    onDescriptionChange={handleDescriptionChange}
                                    onDrop={handleDrop}
                                    onFileSelect={handleSelectTemplateFile}
                                    onResetUpload={resetUpload}
                                    onDownloadTemplate={downloadTemplateFile}
                                    renderSystemTemplateText={renderSystemTemplateText}
                                    headerTitle={headerTitle}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <div className={classes.footerSection}>
                    <Box className={classes.footer}>
                        <Divider />
                        <Box className={classes.footerComp}>
                            {isUpdateTemplateClicked && (
                                <ClassNames key="actionDeleteCss">
                                    {({ css }) => {
                                        const getDeleteAnalyticsValue = () =>
                                            templateList?.isDefault
                                                ? 'MM-Template-Delete-defaultTemplate'
                                                : 'MM-Template-Delete'
                                        return (
                                            <button
                                                key="actionDelete"
                                                data-analytics={getDeleteAnalyticsValue()}
                                                className={`${css`
                                                    ${DeleteButton}
                                                `} ${classes.btnClass} `}
                                                onClick={() => setShowConfirmation(true)}
                                            >
                                                {i18n.t('DELETE')}
                                            </button>
                                        )
                                    }}
                                </ClassNames>
                            )}
                            <AddActionEditActionButton
                                key="actionCancel"
                                data-analytics="MM-Template-Cancel"
                                disableFocusRipple
                                disableRipple
                                disabled={isSubmitting}
                                onClick={toggleDrawer}
                            >
                                {i18n.t('CANCEL')}
                            </AddActionEditActionButton>
                            {!isUpdateTemplateClicked && (
                                <AtlasButton
                                    data-analytics="MM-Template-Save"
                                    role="button"
                                    onKeyPress={(e) => {
                                        if (addKeyPressHandler(e)) {
                                            handleSaveTemplate()
                                        }
                                        return null
                                    }}
                                    disableRipple
                                    className={classes.saveTemplate}
                                    key="actionSave"
                                    disabled={
                                        isSubmitting ||
                                        !items?.file ||
                                        uploadStatus !== TEMPLATE_UPLOAD_STATUS.SUCCESS ||
                                        returnTitleErrorMessage !== null ||
                                        returnDescriptionErrorMessage !== null
                                    }
                                    type="submit"
                                    onClick={handleSaveTemplate}
                                    disableFocusRipple
                                >
                                    {isSubmitting ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        i18n.t('ADD')
                                    )}
                                </AtlasButton>
                            )}
                            {isUpdateTemplateClicked && (
                                <AtlasButton
                                    data-analytics="MM-Template-Update"
                                    role="button"
                                    onKeyPress={(e) => {
                                        if (addKeyPressHandler(e)) {
                                            handleUpdateTemplate(templateList?.id || '', {
                                                title,
                                                description
                                            })
                                        }
                                        return null
                                    }}
                                    disableRipple
                                    className={classes.saveTemplate}
                                    key="actionSave"
                                    disabled={
                                        isSubmitting ||
                                        returnTitleErrorMessage !== null ||
                                        returnDescriptionErrorMessage !== null
                                    }
                                    type="submit"
                                    onClick={() =>
                                        handleUpdateTemplate(templateList?.id || '', {
                                            title,
                                            description
                                        })
                                    }
                                    disableFocusRipple
                                >
                                    {isSubmitting ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        i18n.t('SAVE')
                                    )}
                                </AtlasButton>
                            )}
                        </Box>
                    </Box>
                </div>
                {confirmationDialog}
            </Drawer>
        </>
    )
}

export default TemplateDrawer
